import React, { useCallback, useEffect, useState } from "react";
import {
    Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions,
    DialogContent, DialogTitle, FormControl, Grid, IconButton, InputLabel,
    MenuItem, Paper, Select, TextField, Tooltip, Typography
} from "@mui/material";
import {
    Search as IconSearch, RestartAlt as IconRestart, Visibility as IconVisibility,
    Assessment as IconReporte, Timer as IconTimer, CheckCircle as IconSuccess,
    Error as IconError, TableRows as IconRows, FileDownload as IconDownload,
    Close as IconClose
} from "@mui/icons-material";
import moment from "moment";

import * as bitacoraReporteService from "../../services/bitacoraReporteService";
import DataGridBase from "../../components/common/DataGridBase";
import { PaperComponentDraggable } from "../../components/common/PaperComponentDraggable";

const hoyFecha = () => moment().format("YYYY-MM-DD");

const FILTROS_INICIALES = () => ({
    reportPath: "",
    userName: "",
    format: "",
    status: "",
    fechaInicio: `${hoyFecha()}T00:00`,
    fechaFin: `${hoyFecha()}T23:59`,
});

const OPCIONES_FORMATO = [
    { id: "", label: "(Todos los formatos)" },
    { id: "HTML", label: "HTML (Vista en pantalla)" },
    { id: "PDF", label: "PDF (Exportación)" },
    { id: "EXCEL", label: "Excel (XLS/TSV)" },
    { id: "WORD", label: "Word (DOC)" },
    { id: "PRINT", label: "Impresión directa" },
];

const OPCIONES_ESTADO = [
    { id: "", label: "(Todos los estados)" },
    { id: "rsSuccess", label: "Éxito (rsSuccess)" },
    { id: "ERROR", label: "Errores" },
];

const BitacoraReportes = () => {
    const [cargando, setCargando] = useState(false);
    const [filas, setFilas] = useState([]);
    const [metricas, setMetricas] = useState({
        totalEjecuciones: 0,
        totalFilas: 0,
        tiempoPromedioTotalMs: 0,
        tiempoPromedioDataRetrievalMs: 0,
        tiempoPromedioProcessingMs: 0,
        tiempoPromedioRenderingMs: 0,
        tasaExito: 100,
    });
    const [filtros, setFiltros] = useState(FILTROS_INICIALES);
    const [dialogoParametros, setDialogoParametros] = useState({ abierto: false, datos: null });

    const fetchResultados = useCallback(async (filtrosAplicados) => {
        setCargando(true);
        try {
            const data = await bitacoraReporteService.getsBitacoraReportes(filtrosAplicados);
            const dataMapeada = (data || []).map((r) => ({
                ...r,
                duracionTotal: (r.timeDataRetrieval || 0) + (r.timeProcessing || 0) + (r.timeRendering || 0)
            }));
            setFilas(dataMapeada);

            const kpis = await bitacoraReporteService.getMetricasBitacoraReportes(
                filtrosAplicados.fechaInicio,
                filtrosAplicados.fechaFin
            );
            if (kpis) setMetricas(kpis);
        } catch (err) {
            console.error("Error al consultar bitacora de reportes:", err);
            setFilas([]);
        } finally {
            setCargando(false);
        }
    }, []);

    useEffect(() => {
        fetchResultados(filtros);
    }, [fetchResultados]);

    const handleBuscar = (e) => {
        e.preventDefault();
        fetchResultados(filtros);
    };

    const handleLimpiar = () => {
        const reset = FILTROS_INICIALES();
        setFiltros(reset);
        fetchResultados(reset);
    };

    const handleVerParametros = (item) => {
        setDialogoParametros({
            abierto: true,
            datos: item,
        });
    };

    const colorFormato = (fmt) => {
        switch ((fmt || "").toUpperCase()) {
            case "PDF": return { bg: "#ffebee", color: "#c62828", border: "#ffcdd2" };
            case "EXCEL": return { bg: "#e8f5e9", color: "#2e7d32", border: "#c8e6c9" };
            case "WORD": return { bg: "#e3f2fd", color: "#1565c0", border: "#bbdefb" };
            case "PRINT": return { bg: "#f3e5f5", color: "#6a1b9a", border: "#e1bee7" };
            default: return { bg: "#f5f5f5", color: "#424242", border: "#e0e0e0" };
        }
    };

    const columnas = [
        {
            field: "reportPath",
            headerName: "ReportPath",
            flex: 1.5,
            minWidth: 200,
            renderCell: (params) => (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <IconReporte fontSize="small" sx={{ color: "primary.main" }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: "monospace" }}>
                        {params.value}
                    </Typography>
                </Box>
            )
        },
        {
            field: "userName",
            headerName: "UserName",
            flex: 0.9,
            minWidth: 120,
            renderCell: (params) => (
                <Chip
                    size="small"
                    label={params.value || "ANONIMO"}
                    variant="outlined"
                    sx={{ fontWeight: 500 }}
                />
            )
        },
        {
            field: "format",
            headerName: "Format",
            flex: 0.8,
            minWidth: 100,
            renderCell: (params) => {
                const c = colorFormato(params.value);
                return (
                    <Chip
                        size="small"
                        label={params.value || "HTML"}
                        sx={{
                            backgroundColor: c.bg,
                            color: c.color,
                            border: `1px solid ${c.border}`,
                            fontWeight: 700
                        }}
                    />
                );
            }
        },
        {
            field: "reportAction",
            headerName: "ReportAction",
            flex: 0.8,
            minWidth: 110,
        },
        {
            field: "timeStart",
            headerName: "TimeStart",
            flex: 1.1,
            minWidth: 150,
            renderCell: (params) => (
                <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                    {params.value ? moment(params.value).format("DD/MM/YYYY HH:mm:ss") : ""}
                </Typography>
            )
        },
        {
            field: "duracionTotal",
            headerName: "Duración Total",
            flex: 0.9,
            minWidth: 120,
            type: "number",
            headerAlign: "left",
            align: "left",
            renderCell: (params) => {
                const total = params?.value ?? 0;
                const r = params?.row || {};
                return (
                    <Tooltip title={`Data Retrieval: ${r.timeDataRetrieval || 0}ms | Processing: ${r.timeProcessing || 0}ms | Rendering: ${r.timeRendering || 0}ms`}>
                        <Chip
                            size="small"
                            icon={<IconTimer fontSize="small" />}
                            label={`${total} ms`}
                            color={total > 2000 ? "warning" : "default"}
                            variant="outlined"
                            sx={{ fontWeight: 600 }}
                        />
                    </Tooltip>
                );
            }
        },
        {
            field: "timeDataRetrieval",
            headerName: "Data Retrieval (ms)",
            flex: 0.8,
            minWidth: 120,
        },
        {
            field: "timeProcessing",
            headerName: "Processing (ms)",
            flex: 0.8,
            minWidth: 110,
        },
        {
            field: "timeRendering",
            headerName: "Rendering (ms)",
            flex: 0.8,
            minWidth: 110,
        },
        {
            field: "rowCount",
            headerName: "RowCount",
            flex: 0.7,
            minWidth: 90,
            renderCell: (params) => (
                <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary" }}>
                    {(params.value || 0).toLocaleString()}
                </Typography>
            )
        },
        {
            field: "status",
            headerName: "Status",
            flex: 0.9,
            minWidth: 120,
            renderCell: (params) => {
                const esExito = String(params.value).toLowerCase() === "rssuccess";
                return (
                    <Chip
                        size="small"
                        icon={esExito ? <IconSuccess fontSize="small" /> : <IconError fontSize="small" />}
                        label={params.value || "rsSuccess"}
                        color={esExito ? "success" : "error"}
                        variant={esExito ? "outlined" : "filled"}
                        sx={{ fontWeight: 600 }}
                    />
                );
            }
        },
        {
            field: "acciones",
            headerName: "Parameters",
            flex: 0.7,
            minWidth: 90,
            sortable: false,
            renderCell: (params) => (
                <Tooltip title="Ver Parámetros">
                    <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleVerParametros(params.row)}
                    >
                        <IconVisibility fontSize="small" />
                    </IconButton>
                </Tooltip>
            )
        }
    ];

    const exportarCsv = () => {
        if (!filas.length) return;
        const encabezados = ["Id", "ReportPath", "UserName", "Format", "ReportAction", "TimeStart", "TimeEnd", "TimeDataRetrieval", "TimeProcessing", "TimeRendering", "Status", "RowCount", "Parameters"];
        const lineas = [encabezados.join(",")];

        filas.forEach((r) => {
            const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
            lineas.push([
                r.id,
                escape(r.reportPath),
                escape(r.userName),
                escape(r.format),
                escape(r.reportAction),
                escape(r.timeStart),
                escape(r.timeEnd),
                r.timeDataRetrieval || 0,
                r.timeProcessing || 0,
                r.timeRendering || 0,
                escape(r.status),
                r.rowCount || 0,
                escape(r.parameters)
            ].join(","));
        });

        const blob = new Blob(["\ufeff" + lineas.join("\n")], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `bitacora_reportes_${moment().format("YYYYMMDD_HHmm")}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <Box sx={{ padding: { xs: 1.5, sm: 2.5 } }}>
            {/* Cabecera */}
            <Box sx={{
                background: "linear-gradient(135deg, #0d47a1 0%, #1976d2 50%, #311b92 100%)",
                color: "white",
                borderRadius: 2,
                padding: { xs: 2, sm: 3 },
                marginBottom: 3,
                boxShadow: "0 4px 20px rgba(0,0,0,0.12)"
            }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <IconReporte sx={{ fontSize: 44, opacity: 0.95 }} />
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: 0.5 }}>
                                Bitácora de Reportes
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.3 }}>
                                Control centralizado de ejecuciones basado en el estándar SSIS/SSRS ExecutionLog
                            </Typography>
                        </Box>
                    </Box>
                    <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<IconDownload />}
                        onClick={exportarCsv}
                        disabled={!filas.length}
                        sx={{ textTransform: "none", fontWeight: 600, backgroundColor: "#ffffff", color: "#0d47a1", "&:hover": { backgroundColor: "#f0f0f0" } }}
                    >
                        Exportar CSV
                    </Button>
                </Box>
            </Box>

            {/* Metricas / KPIs */}
            <Grid container spacing={2} sx={{ marginBottom: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card variant="outlined" sx={{ borderRadius: 2, borderColor: "#e0e0e0" }}>
                        <CardContent sx={{ display: "flex", alignItems: "center", gap: 2, padding: "16px !important" }}>
                            <Box sx={{ p: 1.2, borderRadius: 2, backgroundColor: "#e3f2fd", color: "#1976d2" }}>
                                <IconReporte sx={{ fontSize: 32 }} />
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: "uppercase" }}>
                                    Total Ejecuciones
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                    {metricas.totalEjecuciones?.toLocaleString() || 0}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card variant="outlined" sx={{ borderRadius: 2, borderColor: "#e0e0e0" }}>
                        <CardContent sx={{ display: "flex", alignItems: "center", gap: 2, padding: "16px !important" }}>
                            <Box sx={{ p: 1.2, borderRadius: 2, backgroundColor: "#ede7f6", color: "#512da8" }}>
                                <IconTimer sx={{ fontSize: 32 }} />
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: "uppercase" }}>
                                    Tiempo Promedio
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                    {metricas.tiempoPromedioTotalMs || 0} <Typography component="span" variant="body2" color="text.secondary">ms</Typography>
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card variant="outlined" sx={{ borderRadius: 2, borderColor: "#e0e0e0" }}>
                        <CardContent sx={{ display: "flex", alignItems: "center", gap: 2, padding: "16px !important" }}>
                            <Box sx={{ p: 1.2, borderRadius: 2, backgroundColor: "#e8f5e9", color: "#2e7d32" }}>
                                <IconRows sx={{ fontSize: 32 }} />
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: "uppercase" }}>
                                    Filas Devueltas
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                    {metricas.totalFilas?.toLocaleString() || 0}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card variant="outlined" sx={{ borderRadius: 2, borderColor: "#e0e0e0" }}>
                        <CardContent sx={{ display: "flex", alignItems: "center", gap: 2, padding: "16px !important" }}>
                            <Box sx={{ p: 1.2, borderRadius: 2, backgroundColor: "#e8f5e9", color: "#388e3c" }}>
                                <IconSuccess sx={{ fontSize: 32 }} />
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: "uppercase" }}>
                                    Tasa Éxito
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 700, color: "#2e7d32" }}>
                                    {metricas.tasaExito ?? 100}%
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Panel de Filtros */}
            <Card variant="outlined" sx={{ borderRadius: 2, marginBottom: 3, padding: 2.5 }}>
                <form onSubmit={handleBuscar}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={6} md={2.5}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Desde (TimeStart)"
                                type="datetime-local"
                                value={filtros.fechaInicio}
                                onChange={(e) => setFiltros({ ...filtros, fechaInicio: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={2.5}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Hasta (TimeEnd)"
                                type="datetime-local"
                                value={filtros.fechaFin}
                                onChange={(e) => setFiltros({ ...filtros, fechaFin: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={2.5}>
                            <TextField
                                fullWidth
                                size="small"
                                label="ReportPath (Ruta o Nombre)"
                                placeholder="ej. /Reporte/Terapias"
                                value={filtros.reportPath}
                                onChange={(e) => setFiltros({ ...filtros, reportPath: e.target.value })}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={2}>
                            <TextField
                                fullWidth
                                size="small"
                                label="UserName"
                                placeholder="ej. ADMIN"
                                value={filtros.userName}
                                onChange={(e) => setFiltros({ ...filtros, userName: e.target.value })}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={2.5}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Formato</InputLabel>
                                <Select
                                    label="Formato"
                                    value={filtros.format}
                                    onChange={(e) => setFiltros({ ...filtros, format: e.target.value })}
                                >
                                    {OPCIONES_FORMATO.map((o) => (
                                        <MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Estado</InputLabel>
                                <Select
                                    label="Estado"
                                    value={filtros.status}
                                    onChange={(e) => setFiltros({ ...filtros, status: e.target.value })}
                                >
                                    {OPCIONES_ESTADO.map((o) => (
                                        <MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={4} sx={{ display: "flex", gap: 1 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                type="submit"
                                startIcon={<IconSearch />}
                                sx={{ textTransform: "none", fontWeight: 600 }}
                            >
                                Buscar
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={handleLimpiar}
                                startIcon={<IconRestart />}
                                sx={{ textTransform: "none" }}
                            >
                                Limpiar
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            </Card>

            {/* Tabla de Resultados */}
            <Box sx={{ height: 550, width: "100%" }}>
                <DataGridBase
                    rows={filas}
                    columns={columnas}
                    loading={cargando}
                    pageSize={15}
                    pageSizeOptions={[10, 15, 25, 50]}
                    disableSelectionOnClick
                />
            </Box>

            {/* Dialogo para ver parametros completos de ejecucion */}
            <Dialog
                open={dialogoParametros.abierto}
                onClose={() => setDialogoParametros({ abierto: false, datos: null })}
                PaperComponent={PaperComponentDraggable}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ cursor: "move", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <IconReporte color="primary" />
                        <Typography variant="h6">Parámetros de Ejecución</Typography>
                    </Box>
                    <IconButton size="small" onClick={() => setDialogoParametros({ abierto: false, datos: null })}>
                        <IconClose />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {dialogoParametros.datos ? (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            <Box>
                                <Typography variant="caption" color="text.secondary">ReportPath:</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: "monospace" }}>
                                    {dialogoParametros.datos.reportPath}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Usuario y Fecha:</Typography>
                                <Typography variant="body2">
                                    {dialogoParametros.datos.userName} &bull; {moment(dialogoParametros.datos.timeStart).format("DD/MM/YYYY HH:mm:ss")}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Valores de Parámetros (Parameters):</Typography>
                                <Paper variant="outlined" sx={{ p: 2, mt: 0.5, backgroundColor: "#fafafa", maxHeight: 220, overflow: "auto" }}>
                                    <pre style={{ margin: 0, fontFamily: "monospace", fontSize: "0.85rem", whiteSpace: "pre-wrap" }}>
                                        {dialogoParametros.datos.parameters
                                            ? (() => {
                                                try {
                                                    return JSON.stringify(JSON.parse(dialogoParametros.datos.parameters), null, 2);
                                                } catch {
                                                    return dialogoParametros.datos.parameters;
                                                }
                                            })()
                                            : "(Sin parámetros / ejecución directa)"}
                                    </pre>
                                </Paper>
                            </Box>
                        </Box>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogoParametros({ abierto: false, datos: null })}>
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default BitacoraReportes;

